import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contex/AuthContext";
import { toggleTask } from "../lib/db";
import { useTasks, useNotes, useProfile, useCourses } from "../hooks/useStudyOS";

export default function HomeScreen() {
  const { profile: authProfile } = useAuth();
  const { pending } = useTasks();
  const { notes } = useNotes();
  const { profile: liveProfile } = useProfile();
  const navigate = useNavigate();
  const profile = liveProfile || authProfile;
  const { getCourse } = useCourses();
const briefingTasks = pending.filter(t => t.due === "Today" || t.due === "Tomorrow").slice(0, 5);

  const h = new Date().getHours();
  const greeting = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";

  const duePill = (due) => {
    if (due === "Today") return { background:"rgba(240,93,93,.15)", color:"#F05D5D" };
    if (due === "Tomorrow") return { background:"rgba(240,164,41,.15)", color:"#F0A429" };
    return { background:"rgba(255,255,255,.07)", color:"#9998A0" };
  };

  return (
    <div className="screen-wrap">
      <div style={styles.banner}>
  <div style={styles.bannerName}>{greeting}, {profile?.name || "Student"} 👋</div>
  <div style={styles.bannerSub}>{profile?.school || "Let's have a productive day."}</div>
</div>

{/* Morning briefing */}
{briefingTasks.length > 0 && (
  <div style={styles.briefing}>
    <div style={styles.briefingTitle}>📋 Today's briefing</div>
    {briefingTasks.map(t => {
      const course = getCourse(t.courseId);
      const isToday = t.due === "Today";
      return (
        <div key={t.id} style={styles.briefingItem}>
          <div style={{...styles.briefingDot, background: isToday ? "var(--red)" : "var(--amber)"}}/>
          <div style={styles.briefingInfo}>
            <div style={styles.briefingName}>{t.name}</div>
            {course && <div style={{fontSize:"11px", color: course.color}}>{course.name}</div>}
          </div>
          <span style={{...styles.briefingBadge, color: isToday ? "var(--red)" : "var(--amber)", background: isToday ? "var(--red-bg)" : "var(--amber-bg)"}}>
            {t.due}
          </span>
        </div>
      );
    })}
  </div>
)}

      <div style={styles.statGrid}>
        <div style={{...styles.statCard, background:"rgba(124,110,248,.12)", border:"1px solid rgba(124,110,248,.3)"}}>
          <div style={{...styles.statVal, color:"#9D92FA"}}>{pending.length}</div>
          <div style={styles.statLbl}>Tasks pending</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{liveProfile?.timerStreak || 0} 🔥</div>
          <div style={styles.statLbl}>Day streak</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{notes.length}</div>
          <div style={styles.statLbl}>Notes saved</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{liveProfile?.totalSessions || 0}</div>
          <div style={styles.statLbl}>Focus sessions</div>
        </div>
      </div>

      <div style={styles.sLabel}>Today's tasks</div>
      {pending.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>✅</div>
          <div style={styles.emptyTitle}>All clear!</div>
          <div style={styles.emptyDesc}>No pending tasks. Tap Tasks to add some.</div>
          <button style={styles.emptyBtn} onClick={() => navigate("/tasks")}>Go to Tasks</button>
        </div>
      ) : (
        <>
          {pending.slice(0, 3).map(t => (
            <div key={t.id} style={styles.taskCard}>
              <div style={styles.check} onClick={() => toggleTask(t.id, t.done)} />
              <div style={styles.taskBody}>
                <div style={styles.taskName}>{t.name}</div>
                <div style={styles.taskMeta}>{t.due || "No due date"}</div>
              </div>
              {t.due && <span style={{...styles.pill, ...duePill(t.due)}}>{t.due}</span>}
            </div>
          ))}
          {pending.length > 3 && (
            <div style={styles.moreText} onClick={() => navigate("/tasks")}>
              +{pending.length - 3} more tasks →
            </div>
          )}
        </>
      )}

      <div style={styles.sLabel}>Upcoming deadlines</div>
      {pending.filter(t => t.due).length === 0 ? (
        <div style={styles.noDeadlines}>No deadlines set yet.</div>
      ) : (
        <div style={styles.card}>
          {pending.filter(t => t.due).slice(0, 4).map((t, i, arr) => (
            <div key={t.id} style={{...styles.dlRow, borderBottom: i < arr.length-1 ? "1px solid rgba(255,255,255,0.07)" : "none"}}>
              <div style={{...styles.dlDot, background:"#7C6EF8"}} />
              <div style={styles.dlInfo}>
                <div style={styles.dlName}>{t.name}</div>
              </div>
              <div style={styles.dlDate}>{t.due}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  banner:      { background:"var(--accent-bg)", border:"1px solid var(--accent-border)", borderRadius:"16px", padding:"18px 20px", marginBottom:"18px" },
  bannerName:  { fontSize:"22px", fontWeight:700, color:"var(--text)", letterSpacing:"-.5px" },
  bannerSub:   { fontSize:"13px", color:"var(--text2)", marginTop:"4px" },
  statGrid:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"6px" },
  statCard:    { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"14px 16px" },
  statVal:     { fontSize:"22px", fontWeight:700, color:"var(--text)", letterSpacing:"-.5px" },
  statLbl:     { fontSize:"12px", color:"var(--text2)", marginTop:"3px" },
  sLabel:      { fontSize:"11px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".08em", margin:"22px 0 10px" },
  taskCard:    { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"13px 14px", marginBottom:"8px", display:"flex", alignItems:"center", gap:"12px" },
  check:       { width:"22px", height:"22px", borderRadius:"50%", border:"1.5px solid var(--border2)", flexShrink:0, cursor:"pointer" },
  taskBody:    { flex:1 },
  taskName:    { fontSize:"14px", fontWeight:500, color:"var(--text)" },
  taskMeta:    { fontSize:"11px", color:"var(--text3)", marginTop:"3px" },
  pill:        { fontSize:"11px", fontWeight:500, padding:"3px 9px", borderRadius:"20px", flexShrink:0 },
  moreText:    { fontSize:"12px", color:"var(--accent)", textAlign:"center", padding:"8px", cursor:"pointer" },
  card:        { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"4px 16px" },
  dlRow:       { display:"flex", alignItems:"center", gap:"12px", padding:"10px 0" },
  dlDot:       { width:"8px", height:"8px", borderRadius:"50%", flexShrink:0 },
  dlInfo:      { flex:1 },
  dlName:      { fontSize:"13px", fontWeight:500, color:"var(--text)" },
  dlDate:      { fontSize:"12px", fontWeight:600, color:"var(--text2)" },
  empty:       { textAlign:"center", padding:"32px 20px" },
  emptyIcon:   { fontSize:"36px", marginBottom:"10px" },
  emptyTitle:  { fontSize:"16px", fontWeight:600, color:"var(--text)", marginBottom:"6px" },
  emptyDesc:   { fontSize:"13px", color:"var(--text2)", marginBottom:"16px" },
  emptyBtn:    { padding:"10px 24px", background:"var(--accent)", border:"none", borderRadius:"10px", color:"#fff", fontFamily:"inherit", fontSize:"14px", fontWeight:600, cursor:"pointer" },
  noDeadlines: { fontSize:"13px", color:"var(--text3)", padding:"12px 0" },
  briefing:      { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"14px 16px", marginBottom:"16px" },
briefingTitle: { fontSize:"12px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"10px" },
briefingItem:  { display:"flex", alignItems:"center", gap:"10px", padding:"7px 0", borderBottom:"1px solid var(--border)" },
briefingDot:   { width:"7px", height:"7px", borderRadius:"50%", flexShrink:0 },
briefingInfo:  { flex:1 },
briefingName:  { fontSize:"13px", fontWeight:500, color:"var(--text)" },
briefingBadge: { fontSize:"11px", fontWeight:600, padding:"2px 8px", borderRadius:"20px", flexShrink:0 },
};